import { CountryLocaleMap, PAYMENT_EXCEPTION_CODES, type CountryCode } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { type ECommercePaymentRequest } from '@hooks-api/types/apiRequest'
import { type ECommerceOrderType, type ECommercePayment, type UserPaymentTokenInfo } from '@hooks-api/types/dataModel/eCommercePayments'
import { type PaymentMethod, type PaymentMethodType } from '@hooks-api/types/dataModel/payment'
import { BaseCheckoutPayFlowModule } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'
import { getCkoMountContainerId } from '@payments/lib/checkout-pay/ckoMountTarget'
import { isCkoFlowModule } from '@payments/lib/checkout-pay/isCkoFlowModule'
import { ECPayCreditCardModule } from '@payments/lib/ECPayCreditCardModule'
import { getActivePaymentModule } from '@payments/lib/getActivePaymentModule'
import { requiresPaymentModule } from '@payments/lib/paymentModuleMap'
import { TappayApplePayModule } from '@payments/lib/TappayApplePayModule'
import { TappayCreditCardModule } from '@payments/lib/TappayCreditCardModule'
import { type PaymentModule, type PaymentSetting, type ApplePayPaymentDetails as RawApplePayPaymentDetails } from '@payments/lib/types'
import { AxiosError } from 'axios'
import { type PaymentBaseMemoryStore, type PaymentBaseStore } from '../types'
import { checkoutPayFlowController } from './checkoutPayFlowController'
import { CkoMountContainerDetachedError } from './cko-mount-container-detached-error'

export const setAppliedPaymentMethodId = (state: PaymentBaseStore, appliedPaymentMethodId: number, appliedPaymentMethodType: PaymentMethodType | null = null): PaymentBaseStore => {
  return {
    ...state,
    appliedPaymentMethodId,
    appliedPaymentMethodType,
  }
}

export const setAppliedInstallment = (state: PaymentBaseStore, appliedInstallment: number | null): PaymentBaseStore => {
  return {
    ...state,
    appliedInstallment,
  }
}

export const setRememberPaymentCardToken = (state: PaymentBaseStore, rememberPaymentCardToken: boolean): PaymentBaseStore => {
  return {
    ...state,
    rememberPaymentCardToken,
  }
}

export const resetUserPaymentTokenInfo = (state: PaymentBaseStore): PaymentBaseStore => {
  return {
    ...state,
    userPaymentTokenInfo: null,
  }
}

export const setCreditCardAffiliateCode = (state: PaymentBaseMemoryStore, creditCardAffiliateCode: string | null): PaymentBaseMemoryStore => {
  return {
    ...state,
    creditCardAffiliateCode,
  }
}

export const resetCreditCardAffiliateCode = (state: PaymentBaseMemoryStore): PaymentBaseMemoryStore => {
  return {
    ...state,
    creditCardAffiliateCode: null,
  }
}

/**
 * 強制驗證付款方式必須是 credit card 才會設定 userPaymentTokenInfo
 */
export const setUserPaymentTokenInfo = (state: PaymentBaseStore, userPaymentTokenInfo: UserPaymentTokenInfo | null, isCreditCardPaymentMethod: boolean): PaymentBaseStore => {
  if (!isCreditCardPaymentMethod) return state
  return {
    ...state,
    userPaymentTokenInfo,
  }
}

export const setBankCode = (state: PaymentBaseStore, bankCode: string | null): PaymentBaseStore => {
  return {
    ...state,
    bankCode,
  }
}

export const setCardSource = (state: PaymentBaseStore, cardSource: string): PaymentBaseStore => {
  return {
    ...state,
    cardSource,
  }
}

export interface ApplePayPaymentDetails {
  countryCode: RawApplePayPaymentDetails['countryCode']
  orderTotalAmount: RawApplePayPaymentDetails['orderTotalAmount']
  currency: RawApplePayPaymentDetails['currency']
  onCancel?: RawApplePayPaymentDetails['onCancel']
  onValidateMerchant?: RawApplePayPaymentDetails['onValidateMerchant']
}

interface ApplyCkoSessionOptions {
  /** dialog 內 stored_card 的容器；覆寫 configureBeforeMount 推導的頁面級 id。 */
  storedCardContainerId?: string
  /** 站台語系，決定 CKO SDK 顯示的語言；未提供時 SDK 走 zh-TW 預設。 */
  countryCode?: CountryCode
}

/** 代表這一次的掛載，由呼叫端持有並在結束時呼叫 `unmount()`。 */
export interface CkoMount {
  unmount: () => void
}

const createCkoMount = (paymentModule: BaseCheckoutPayFlowModule): CkoMount => {
  let isUnmounted = false
  // 綁定建立當下的掛載目標：module 為 singleton，被淘汰週期晚到的 handle 不得拆掉後續新掛載的元件。
  const ownedElement = paymentModule.getMountedContainerElement()
  return {
    unmount: () => {
      if (isUnmounted) return
      isUnmounted = true
      if (paymentModule.getMountedContainerElement() !== ownedElement) return
      paymentModule.unmount()
    },
  }
}

export const applyCkoSession = async (
  paymentModule: BaseCheckoutPayFlowModule,
  appliedPaymentInfo: ECommercePayment,
  appEnv: 'production' | 'sandbox',
  overrideContainerId?: string,
  options?: ApplyCkoSessionOptions,
): Promise<CkoMount> => {
  const { app_key, checkoutpay_session_info } = appliedPaymentInfo.payment_service_provider_info

  const containerId = overrideContainerId ?? getCkoMountContainerId(appliedPaymentInfo.payment_method)
  if (!containerId) throw new Error(`[applyCkoSession] no CKO mount target for method type ${appliedPaymentInfo.payment_method.type}`)

  if (!app_key) throw new Error('[applyCkoSession] CKO Flow app_key is required')

  const locale = options?.countryCode ? CountryLocaleMap[options.countryCode] : undefined
  paymentModule.configurePayment({ appKey: app_key, appEnv, locale })

  // shouldPreserveMountedState() 為 true 時直接 return，避免重掛清除使用者已輸入的卡號。
  // 但 module 是 singleton，尚未改用 CkoMount 的呼叫端關閉畫面時不會卸載，component 留著會讓它
  // 一直回 true。因此再加 isMounted() 確認掛載的 node 仍在畫面上，否則重新開啟時會是空白。
  if (paymentModule.isMounted() && paymentModule.shouldPreserveMountedState()) return createCkoMount(paymentModule)

  // Same session + SDK already rendered → skip unmount/remount to avoid visual flash.
  const incomingSessionId = checkoutpay_session_info?.id ?? null
  const currentSessionId = paymentModule.getPaymentSessionId()
  if (incomingSessionId && incomingSessionId === currentSessionId && paymentModule.isMounted()) {
    return createCkoMount(paymentModule)
  }

  // Same session + mounting in progress（React StrictMode 同 session 觸發兩次 effect）：
  // 若 unmount 再重掛，兩個 mountInternal 並跑競速壞狀態。
  if (incomingSessionId && incomingSessionId === currentSessionId && paymentModule.isMounting()) {
    await paymentModule.awaitMount()
    return createCkoMount(paymentModule)
  }

  if (checkoutpay_session_info) {
    paymentModule.setPaymentSession(checkoutpay_session_info)
  }
  else if (appliedPaymentInfo.payment_session) {
    paymentModule.setPaymentSession(appliedPaymentInfo.payment_session)
  }
  else {
    throw new Error('[applyCkoSession] missing CKO paymentSession (checkoutpay_session_info / payment_session)')
  }

  paymentModule.unmount()
  paymentModule.configureBeforeMount(appliedPaymentInfo.payment_method, {
    storedCardContainerId: options?.storedCardContainerId,
  })
  await paymentModule.mount(`#${containerId}`)
  return createCkoMount(paymentModule)
}

interface CreditCardDialogMountParams {
  containerId: string
  storedCardContainerId: string
  countryCode?: CountryCode
}

/**
 * 信用卡 dialog 的 mount 入口，各線一律走這裡，不要自組 `applyCkoSession` 的 options。
 * 後置條件放在這裡而非 `applyCkoSession` 內，因為後者的早退檢查都在 `mount()` 之前。
 */
export const applyCkoSessionForCreditCardDialog = async (
  paymentModule: BaseCheckoutPayFlowModule,
  appliedPaymentInfo: ECommercePayment,
  appEnv: 'production' | 'sandbox',
  { containerId, storedCardContainerId, countryCode }: CreditCardDialogMountParams,
): Promise<CkoMount> => {
  /** 記住掛載當下的容器，用來區分「容器中途被移除（可重試）」與「真正掛載失敗」。 */
  const containerAtMountStart = typeof document === 'undefined' ? null : document.getElementById(containerId)
  const ckoMount = await applyCkoSession(paymentModule, appliedPaymentInfo, appEnv, containerId, { storedCardContainerId, countryCode })

  if (!paymentModule.isMounted()) {
    if (containerAtMountStart != null && !containerAtMountStart.isConnected) {
      throw new CkoMountContainerDetachedError(containerId)
    }
    // 診斷資訊已在 message 內；不在此記 datadog，交由 funnel（reportFailure）單點記錄，避免同一錯誤記兩次。
    throw new Error(`[applyCkoSessionForCreditCardDialog] mount skipped — component unavailable or container #${containerId} missing`)
  }
  // module 是 singleton：該線若還在頁面 inline 掛同一個 module，applyCkoSession 會在 mount() 前
  // 就早退，isMounted() 會通過 —— 只有掛載目標對不上，所以必須比對。
  const mountedSelector = paymentModule.getMountedContainerSelector()
  if (![`#${containerId}`, `#${storedCardContainerId}`].includes(mountedSelector ?? '')) {
    // 診斷資訊已在 message 內；不在此記 datadog，交由 funnel（reportFailure）單點記錄，避免同一錯誤記兩次。
    throw new Error(`[applyCkoSessionForCreditCardDialog] SDK is mounted at ${mountedSelector} instead of the dialog container #${containerId} — this line is still mounting the method inline, so the dialog would render empty`)
  }
  return ckoMount
}

interface CreditCardDialogSubmitParams {
  paymentTransactionId: string | number | null | undefined
  orderHash: string | null | undefined
}

/**
 * 信用卡 dialog 的送出入口，收斂送出前處理，呼叫端不需自組 controller 參數。
 */
export const submitCreditCardDialogPayment = async (
  paymentModule: BaseCheckoutPayFlowModule,
  { paymentTransactionId, orderHash }: CreditCardDialogSubmitParams,
): Promise<string> => {
  return checkoutPayFlowController.submitAndAwaitPaymentId(paymentModule, { paymentTransactionId, orderHash })
}

export const configPaymentModule = ({
  appEnv,
  appliedPaymentInfo,
  applePayPaymentDetails,
  countryCode,
}: {
  appEnv: 'production' | 'sandbox'
  appliedPaymentInfo: ECommercePayment
  applePayPaymentDetails?: ApplePayPaymentDetails
  countryCode?: CountryCode
}) => {
  const paymentMethod = appliedPaymentInfo.payment_method
  if (!paymentMethod.service_type) return

  const paymentModule = getActivePaymentModule(paymentMethod.service_type, paymentMethod.type)
  if (!paymentModule) {
    if (requiresPaymentModule(paymentMethod.type)) {
      // 只印識別欄位，避免把 payment_session token / app_key 等敏感資料寫進 log
      console.error('[configPaymentModule] Payment module not found for', {
        service_type: paymentMethod.service_type,
        type: paymentMethod.type,
      })
    }
    return
  }

  try {
    const { app_id, app_key, app_pay_identifier, ecpay_token } = appliedPaymentInfo.payment_service_provider_info
    if (paymentModule instanceof TappayApplePayModule) {
      if (!app_id || !app_key || !applePayPaymentDetails) {
        throw new Error('Apple Pay 的 appId、appKey 和 applePayPaymentDetails 是必填的')
      }
      paymentModule.configurePayment({
        appId: app_id,
        appKey: app_key,
        appEnv,
        merchantIdentifier: app_pay_identifier,
        displayItems: [{
          label: '總計',
          amount: {
            currency: applePayPaymentDetails.currency,
            value: String(applePayPaymentDetails.orderTotalAmount),
          },
        }],
        ...applePayPaymentDetails,
      })
    }
    else if (paymentModule instanceof BaseCheckoutPayFlowModule) {
      if (paymentModule.mountsInDialog()) {
        paymentModule.configurePayment({ appKey: app_key, appEnv, locale: countryCode ? CountryLocaleMap[countryCode] : undefined })
        return
      }
      applyCkoSession(paymentModule, appliedPaymentInfo, appEnv, undefined, { countryCode }).catch((error) => {
        console.error('[configPaymentModule] CKO apply session error', error)
        datadogRum.addError(error, { function: '[configPaymentModule] CKO apply session error' })
      })
    }
    else if (paymentModule instanceof ECPayCreditCardModule) {
      if (!ecpay_token) {
        throw new Error('ECPay 的 token 是必填的')
      }
      paymentModule.configurePayment({
        appKey: ecpay_token,
        appEnv,
      })
    }
    else {
      paymentModule.configurePayment({
        appId: app_id,
        appKey: app_key,
        appEnv,
      })
    }
    console.debug('[configPaymentModule] configure success')
  }
  catch (error) {
    console.error('[configPaymentModule] configure error', error)
  }
}

export const initPaymentsSdk = async (paymentMethods: PaymentMethod[]) => {
  const results = await Promise.allSettled(
    paymentMethods.map(async (payment) => {
      if (!payment.service_type || !payment.type) {
        return
      }
      const paymentModule = getActivePaymentModule(payment.service_type, payment.type)
      if (!paymentModule) return
      await paymentModule.loadScript()
    }),
  )
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.error(`[initPaymentsSdk] Failed to load SDK for payment method: ${paymentMethods[index]?.type}`, result.reason)
      datadogRum.addError(result.reason, {
        function: 'initPaymentsSdk',
        payment_method_type: paymentMethods[index]?.type,
      })
    }
  })
}

type PrimePaymentModule = Pick<PaymentModule<PaymentSetting>, 'getPrime'>

/** 卡片識別碼要跟同一次 getPrime 一起讀，才不會帶到上一張卡留在 singleton 的值 */
export const getPrimeFromPaymentModule = async (paymentModule: PrimePaymentModule) => {
  const prime = await paymentModule.getPrime()
  if (typeof prime === 'string') {
    const cardIdentifier = paymentModule instanceof TappayCreditCardModule ? paymentModule.getCardIdentifier() : undefined
    return { paymentPrime: prime, cardIdentifier }
  }
  else if ('paymentData' in prime) {
    return { applePayInfo: prime }
  }
  return null
}

export const getPaymentModulePrime = async (appliedPaymentMethod?: PaymentMethod) => {
  const serviceType = appliedPaymentMethod?.service_type
  if (!serviceType) return null
  const paymentModule = getActivePaymentModule(serviceType, appliedPaymentMethod.type)
  if (!paymentModule) return null
  // Flow SDK 走 session-based lifecycle，不該被 dispatch 進來。
  if (isCkoFlowModule(paymentModule)) {
    const error = new Error('[getPaymentModulePrime] called with a CKO Flow module — prime should come from the Flow session lifecycle')
    datadogRum.addError(error, { function: '[getPaymentModulePrime] misuse with CKO Flow module' })
    throw error
  }
  return getPrimeFromPaymentModule(paymentModule)
}

export type PaymentModulePrime = Awaited<ReturnType<typeof getPaymentModulePrime>>

/**
 * 回 null 代表這個付款方式不可能有聯名卡優惠（非 TapPay 信用卡），呼叫端直接視為不符、不必打 API。
 *
 * 新輸入的卡要先取一次 prime，發卡行代碼才拿得到，順序不可反。
 */
export const getCardIssuerLookupPayload = async (
  appliedPaymentMethod: PaymentMethod | undefined,
  userPaymentTokenInfo: UserPaymentTokenInfo | null,
) => {
  const serviceType = appliedPaymentMethod?.service_type
  if (!serviceType) return null
  const paymentModule = getActivePaymentModule(serviceType, appliedPaymentMethod.type)
  if (!(paymentModule instanceof TappayCreditCardModule)) return null

  if (userPaymentTokenInfo) {
    return { card_token_id: userPaymentTokenInfo.payment_token_id }
  }
  await paymentModule.getPrime()
  return { bank_id: paymentModule.getCard()?.bank_id ?? null }
}

export const getPaymentMethodsGroupByType = (paymentMethods?: PaymentMethod[]) => {
  if (!paymentMethods) return undefined
  const map = paymentMethods?.reduce<Map<PaymentMethodType, PaymentMethod[]>>((acc, method) => {
    acc.set(method.type, [...(acc.get(method.type) || []), method])
    return acc
  }, new Map<PaymentMethodType, PaymentMethod[]>())
  return [...map]
}

/**
 * 5001 CKO_PAYMENT_RECONCILING：exec 進入時 Redis 分散式鎖衝突，尚未打到 gateway。
 * 另一個 flow 持鎖中，短暫等待後鎖釋放即可重試，線性間隔即足夠。
 */
const DEFAULT_RECONCILIATION_WAITS_MS = [1000, 2000, 3000]

/**
 * 13001 PAYMENT_API_FAILED：請求已打到 CKO gateway 但 API 回傳失敗。
 * CKO 需要時間處理，指數退避避免連打加重錯誤、給 CKO 更長的恢復窗口。
 */
const EXPONENTIAL_RECONCILIATION_WAITS_MS = [1000, 2000, 4000, 8000]

/**
 * waitsMs[0] = 首次 exec 前的初始延遲，讓 CKO webhook 先抵達後端；
 * waitsMs[n] = 第 n 次重試前的等待；陣列長度即最多 exec 次數。
 */
const DEFAULT_CODE_WAIT_STRATEGIES: Record<number, number[]> = {
  [PAYMENT_EXCEPTION_CODES.CKO_PAYMENT_RECONCILING]: DEFAULT_RECONCILIATION_WAITS_MS,
  [PAYMENT_EXCEPTION_CODES.PAYMENT_API_FAILED]: EXPONENTIAL_RECONCILIATION_WAITS_MS,
}

const getPaymentExecutionErrorCode = (error: unknown): number | undefined =>
  error instanceof AxiosError ? error.response?.data?.error?.code : undefined

export interface CkoReconciliationRetryOptions {
  /** 重試等待序列（ms），套用至 5001 及 extraRetryCodes。預設 [1000, 2000, 3000]。 */
  waitsMs?: number[]
  /** 除內建碼外額外重試的錯誤碼，採用 waitsMs 序列。 */
  extraRetryCodes?: number[]
}

/**
 * CKO exec 重試 wrapper，依錯誤碼各自套用等待策略。
 * 各碼計數獨立（retriesByCode）：同一次 exec 序列可能同時遇到 5001 與 13001，
 * 兩者 retry budget 互不消耗，各自耗盡才拋出最後一次 error。
 */
export async function executeWithCkoReconciliationRetry<T>(
  execute: () => Promise<T>,
  { waitsMs = DEFAULT_RECONCILIATION_WAITS_MS, extraRetryCodes = [] }: CkoReconciliationRetryOptions = {},
): Promise<T> {
  const strategies: Record<number, number[]> = {
    ...DEFAULT_CODE_WAIT_STRATEGIES,
    [PAYMENT_EXCEPTION_CODES.CKO_PAYMENT_RECONCILING]: waitsMs,
  }
  for (const code of extraRetryCodes) {
    if (!(code in strategies)) strategies[code] = waitsMs
  }

  const retriesByCode: Record<number, number> = {}

  const initialWaitMs = waitsMs[0] ?? 0
  if (initialWaitMs > 0) await new Promise<void>(resolve => setTimeout(resolve, initialWaitMs))

  while (true) {
    try {
      return await execute()
    }
    catch (error) {
      const code = getPaymentExecutionErrorCode(error)
      if (code === undefined || !(code in strategies)) throw error

      const strategy = strategies[code] ?? []
      const retriesDone = retriesByCode[code] ?? 0
      const nextWaitIndex = retriesDone + 1

      if (nextWaitIndex >= strategy.length) throw error

      const nextWaitMs = strategy[nextWaitIndex] ?? 0
      retriesByCode[code] = retriesDone + 1

      console.debug('[executeWithCkoReconciliationRetry] reconciling, waiting before retry', { code, retry: retriesDone + 1, nextWaitMs })
      if (nextWaitMs > 0) await new Promise<void>(resolve => setTimeout(resolve, nextWaitMs))
    }
  }
}

interface BuildCkoTriggerPayloadArgs {
  countryCode: CountryCode
  orderId: string | number | null | undefined
  paymentMethodId: number
  successUrl: string
  fallbackUrl: string
  orderType?: ECommerceOrderType
  email?: string
  rememberCardToken?: boolean | null
  installment?: number | null
}

// CKO Flow /payment trigger（init）payload 的單一來源，避免 4 個付款方式各自手刻、形狀漂移。
// remember_payment_card_token / installment 只有信用卡有需求，APM 走預設值（不存卡、不分期）；
// 各站點只覆寫真正不同的欄位。
export const buildCkoTriggerPayload = ({
  countryCode,
  orderId,
  paymentMethodId,
  successUrl,
  fallbackUrl,
  orderType,
  email,
  rememberCardToken = false,
  installment = null,
}: BuildCkoTriggerPayloadArgs): ECommercePaymentRequest => ({
  country_code: countryCode,
  order_type: orderType,
  order_id: orderId,
  payment_method_id: paymentMethodId,
  remember_payment_card_token: rememberCardToken,
  installment,
  payment_success_url: successUrl,
  payment_fallback_url: fallbackUrl,
  email,
})
